import './coapplicantEditPersonalPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import {
  EMAIL_ADDRESS_PATTERN,
  PERSON_NAME_PATTERN,
  PHONE_NUMBER_PATTERN,
} from '@thesavingsgroup/regex';
import { toDigits } from '@thesavingsgroup/utils/toDigits';
import { toPhoneString } from '@thesavingsgroup/utils/toPhoneString';
import isEmpty from 'lodash/isEmpty';
import lowerCase from 'lodash/lowerCase';
import trim from 'lodash/trim';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { Input } from '../../../components/Input/Input';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useUserStore from '../../../store/userStore/useUserStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const {
    computed: { coapplicantReviewEditPathname, coapplicantReviewEditProgress },
    coapplicantFirstName,
    coapplicantLastName,
    coapplicantPrimaryEmailAddress,
    coapplicantPrimaryPhoneNumber,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantFirstName,
    setCoapplicantLastName,
    setCoapplicantPrimaryEmailAddress,
    setCoapplicantPrimaryPhoneNumber,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    computed: { userEmailAddress, userPhoneNumber },
  } = useUserStore();

  const form = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      firstNameValue: coapplicantFirstName,
      lastNameValue: coapplicantLastName,
      primaryEmailAddressValue: coapplicantPrimaryEmailAddress,
      primaryPhoneNumberValue: toPhoneString(coapplicantPrimaryPhoneNumber),
    },
    delayError: 200,
    mode: 'onTouched',
    shouldFocusError: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = form;

  const firstNameValidation = {
    required: t('validation.firstName.required'),
    minLength: {
      value: 2,
      message: t('validation.firstName.minLength', { min: 2 }),
    },
    pattern: {
      value: PERSON_NAME_PATTERN,
      message: t('validation.firstName.invalid'),
    },
  };

  const lastNameValidation = {
    required: t('validation.lastName.required'),
    minLength: {
      value: 2,
      message: t('validation.lastName.minLength', { min: 2 }),
    },
    pattern: {
      value: PERSON_NAME_PATTERN,
      message: t('validation.lastName.invalid'),
    },
  };

  const emailAddressValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.emailAddress.required'),
    pattern: {
      value: EMAIL_ADDRESS_PATTERN,
      message: t('validation.emailAddress.invalid'),
    },
    validate: (value: string): string | boolean =>
      lowerCase(value) !== lowerCase(userEmailAddress) ||
      t('validation.emailAddress.applicantConflict'),
  };

  const phoneNumberValidation = {
    setValueAs: (value: string): string => toDigits(value),
    required: t('validation.phone.required'),
    pattern: {
      value: PHONE_NUMBER_PATTERN,
      message: t('validation.phone.invalid'),
    },
    validate: (value: string): string | boolean =>
      lowerCase(value) !== lowerCase(userPhoneNumber) ||
      t('validation.phone.applicantConflict'),
  };

  const finishEditing = (): void => {
    navigate(coapplicantReviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      firstNameValue,
      lastNameValue,
      primaryEmailAddressValue,
      primaryPhoneNumberValue,
    } = values;

    if (
      !firstNameValue ||
      !lastNameValue ||
      !primaryEmailAddressValue ||
      !primaryPhoneNumberValue
    ) {
      return;
    }

    setCoapplicantFirstName(firstNameValue);
    setCoapplicantLastName(lastNameValue);
    setCoapplicantPrimaryEmailAddress(primaryEmailAddressValue);
    setCoapplicantPrimaryPhoneNumber(primaryPhoneNumberValue);

    if (isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        finishEditing();
      }

      return;
    }

    finishEditing();
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="page coapplicant-edit-personal-page">
        <Header
          progress={coapplicantReviewEditProgress}
          showDrawerToggle
          onBackClick={finishEditing}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantEditPersonal.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantEditPersonal.form.h1}>
                {t('CoapplicantEditPersonalPage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantEditPersonal.form.h2}>
                {t('CoapplicantEditPersonalPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantEditPersonal.alert.error
                    }
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-inputs">
                  <div className="input-half">
                    <Input
                      {...register('firstNameValue', firstNameValidation)}
                      data-testid={
                        locator.pages.coapplicantEditPersonal.form
                          .firstNameInput
                      }
                      error={errors.firstNameValue}
                      errorTestId={
                        locator.pages.coapplicantEditPersonal.form
                          .firstNameInputError
                      }
                      label={t('CoapplicantEditPersonalPage.firstName')}
                    />
                  </div>

                  <div className="input-half">
                    <Input
                      {...register('lastNameValue', lastNameValidation)}
                      data-testid={
                        locator.pages.coapplicantEditPersonal.form.lastNameInput
                      }
                      error={errors.lastNameValue}
                      errorTestId={
                        locator.pages.coapplicantEditPersonal.form
                          .lastNameInputError
                      }
                      label={t('CoapplicantEditPersonalPage.lastName')}
                    />
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <NumberInput
                    {...register(
                      'primaryPhoneNumberValue',
                      phoneNumberValidation,
                    )}
                    autoComplete="tel-national"
                    data-testid={
                      locator.pages.coapplicantEditPersonal.form.phoneInput
                    }
                    error={errors.primaryPhoneNumberValue}
                    errorTestId={
                      locator.pages.coapplicantEditPersonal.form.phoneInputError
                    }
                    label={t('CoapplicantEditPersonalPage.phone')}
                    phone
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Input
                    {...register(
                      'primaryEmailAddressValue',
                      emailAddressValidation,
                    )}
                    data-testid={
                      locator.pages.coapplicantEditPersonal.form
                        .emailAddressInput
                    }
                    error={errors.primaryEmailAddressValue}
                    errorTestId={
                      locator.pages.coapplicantEditPersonal.form
                        .emailAddressInputError
                    }
                    label={t('CoapplicantEditPersonalPage.email')}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <Button
                    data-testid={
                      locator.pages.coapplicantEditPersonal.form.cancelButton
                    }
                    variant="secondary"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('CoapplicantEditPersonalPage.cancel')}
                  </Button>

                  <Button
                    data-testid={
                      locator.pages.coapplicantEditPersonal.form.submitButton
                    }
                    disabled={!isValid}
                    loader={isSyncingCoapplicant}
                    fullWidth
                    submit
                  >
                    {t('CoapplicantEditPersonalPage.save')}
                  </Button>
                </div>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantEditPersonal.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantEditPersonalPage';
